<template>
  <div
    class="flex-grow-1 d-flex justify-space-between align-center"
    :data-test-id="`invited-signatory-${signature.email}`"
    @mouseenter="isMouseHover = true"
    @mouseleave="isMouseHover = false"
  >
    <!-- EMAIL AND STATUS TEXT -->
    <div
      class="d-flex flex-column"
      :class="[signatureClass]"
      style="min-width: 0"
    >
      <!-- EMAIL -->
      <div
        class="d-flex align-center justify-space-between signee-email"
        :class="{
          clickable: !isDashBoard && !isDocumentWithdrawn && !isDocumentRejected
        }"
        @click="scrollToSignature"
      >
        <v-tooltip
          :aria-label="signature.email"
          content-class="tooltip deep-light tooltip-top pointer-events-auto bg-white"
          :disabled="!isEllipsisActive"
          location="top"
          :open-on-click="device?.isMobile"
        >
          <template #activator="{ props: ActivatorProps }">
            <div class="d-flex align-center ga-2">
              <VIcon
                v-if="hasApprovers && isSigneeTypeApprove"
                class="text-primary"
              >
                fal fa-octagon-check
              </VIcon>
              <SignIcon
                v-else-if="hasApprovers && isSigneeTypeSign"
                aria-label="Signer"
                class="text-primary"
                role="img"
              />
              <div
                v-bind="ActivatorProps"
                ref="emailElement"
                class="text-truncate invited-signee-email"
                :class="{
                  'email-font pt-0 pb-0': isDashBoard
                }"
                :style="{ maxWidth: `${emailElementWidth}px` }"
              >
                {{ signature.email }}
              </div>
            </div>
          </template>
          <template #default>
            {{ signature.email }}
          </template>
        </v-tooltip>
      </div>
      <!-- /EMAIL -->

      <!-- STATUS TEXT -->
      <div class="d-flex align-center text-body-small mt-1">
        <div class="d-flex align-center">
          <span :class="status.class">
            {{ status.text }}
          </span>
          <span
            v-if="status.time"
            v-tooltip="{
              text: timestampToDate(signature.completionTime),
              location: 'right',
              offset: 4,
              openDelay: 300
            }"
            :aria-label="status.time"
            class="ml-2 text-grey"
          >
            {{ status.time }}
          </span>
          <template v-if="showResendInvitationLink">
            <span aria-hidden="true" class="mx-1 text-grey">|</span>
            <button
              class="pa-0 bg-transparent text-primary text-body-small text-none cursor-pointer border-0"
              data-test-id="resend-invitation-link"
              type="button"
              @click.stop="onResendInvitation"
            >
              {{ i18n.t('buttons.send_reminder') }}
            </button>
          </template>
        </div>
      </div>
      <!-- /STATUS TEXT -->
    </div>
    <!-- /EMAIL AND STATUS TEXT -->

    <!-- STATUS ICONS (menu last so it lines up with expansion-panel chevron) -->
    <div
      class="d-inline-flex justify-end align-center ga-2 flex-shrink-0 participant-actions"
    >
      <v-icon v-if="signeeComment" size="18" @click="handleShowDialog">
        fal fa-comment-lines</v-icon
      >
      <div class="d-flex align-center justify-center">
        <v-icon
          v-if="isSignatureApproved || isSignatureSigned || isSignatureRejected"
          :color="
            isSignatureApproved || isSignatureSigned ? 'success' : 'error'
          "
          size="18"
        >
          {{
            isSignatureApproved || isSignatureSigned
              ? 'fas fa-circle-check'
              : isSignatureRejected
                ? 'fas fa-circle-xmark'
                : ''
          }}
        </v-icon>
      </div>

      <ParticipantMenu
        v-if="
          (!isDashBoard && !isSignatureSigned) ||
          (!isDashBoard && !isSignatureApproved)
        "
        :participant-email="signature.email"
        :participant-id="signature.signeeId"
        :participant-policy="signature.policy"
        :participant-status="signature.signStatus"
        :participant-type="
          signature.signeeType === SigneeType.SIGN ? 'signee' : 'approve'
        "
      />
    </div>
    <!-- /STATUS ICONS -->
  </div>
  <!-- COMMENT DIALOG -->
  <SigneeCommentDialog
    v-if="signeeComment"
    :ref="`comment-dialog-${signature.email}`"
    v-model:show-dialog="showCommentDialog"
    :comment="signeeComment"
    :signee="signature"
  />
  <!-- /COMMENT DIALOG -->
</template>
<script setup lang="ts">
import SigneeCommentDialog from '@/components/dialogs/SigneeCommentDialog.vue';
import SignIcon from '@/components/icons/SignIcon.vue';
import ParticipantMenu from '@/components/menus/ParticipantMenu.vue';
import SignatureService from '@/services/signatureService';
import { isMobileOrTablePortrait } from '@/services/styleService';
import AuthService from '@/services/authService.ts';
import { DocumentStatus } from '@/types/enums/DocumentStatus.ts';
import { SignStatus } from '@/types/enums/SignStatus.ts';
import { useSignStore } from '@/stores/sign.ts';
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { DeviceKey } from '@/plugins/device-detector-js';
import { formatDateToLocale } from '@/composables/useUtils';
import { useScreenOrientation } from '@vueuse/core';
import type { PropType } from 'vue';
import type { SignSignee } from '@/types/SignSignee';
import { SigneeType } from '@/types/enums/SigneeType';
import { timestampToDate } from '@deepcloud/deep-ui-utils/date';
import { useResendInvitations } from '@/composables/useResendInvitations';

const props = defineProps({
  signature: {
    type: Object as PropType<SignSignee>,
    required: true
  },
  hasApprovers: {
    type: Boolean,
    default: false
  },
  isDashBoard: {
    type: Boolean,
    default: false
  },
  isUnordered: {
    type: Boolean,
    default: false
  },
  emailElementWidth: {
    type: Number,
    default: 0
  },
  documentId: {
    type: String,
    default: undefined
  }
});

const display = useDisplay();
const signStore = useSignStore();
const { requestResendForSignee, isPending } = useResendInvitations();
const { orientation } = useScreenOrientation();
const device = inject(DeviceKey);
const i18n = useI18n();
const isMouseHover = ref(false);
const showCommentDialog = ref(false);
const emailElement = ref<HTMLElement>();
const documentStatus = computed(() => {
  return signStore.document?.documentStatus;
});

// Document status
const isDocumentWithdrawn = computed(
  () => documentStatus.value === DocumentStatus.WITHDRAWN
);
const isDocumentRejected = computed(
  () => documentStatus.value === DocumentStatus.REJECTED
);

const isDocumentInProgress = computed(
  () => documentStatus.value === DocumentStatus.IN_PROGRESS
);
//

// Signature status
const isSignatureSigned = computed(
  () => props.signature.signStatus === SignStatus.SIGNED
);
const isSignatureApproved = computed(
  () => props.signature.signStatus === SignStatus.APPROVED
);
const isSignatureRejected = computed(
  () => props.signature.signStatus === SignStatus.REJECTED
);
const isSignaturePending = computed(
  () => props.signature.signStatus === SignStatus.PENDING
);
const isSignatureOnHold = computed(
  () => props.signature.signStatus === SignStatus.ON_HOLD
);
const isSignatureObsolete = computed(
  () => props.signature.signStatus === SignStatus.OBSOLETE
);

// Signature type
const isSigneeTypeApprove = computed(
  () => props.signature.signeeType === SigneeType.APPROVE
);
const isSigneeTypeSign = computed(
  () => props.signature.signeeType === SigneeType.SIGN
);

const isMobileOrTabletPortrait = computed(() => {
  return (
    isMobileOrTablePortrait(
      device?.isMobile,
      device?.isTablet,
      display.mdAndDown.value
    ) && orientation.value === 'portrait-primary'
  );
});

const isMe = computed(() => {
  if (signStore.document?.signContext.email) {
    return SignatureService.compareString(
      signStore.document.signContext.email,
      props.signature?.email
    );
  }
  if (AuthService.isAuthenticated) {
    return SignatureService.compareString(
      AuthService.user?.email,
      props.signature?.email
    );
  } else {
    return false;
  }
});

const signatureClass = computed(() => {
  if (isMe.value && isSignaturePending.value) return 'my-card';
  if (isSignaturePending.value) return 'pending-card';
  if (isSignatureSigned.value) return 'signed-card';
  return '';
});

const status = computed(() => {
  // The signee's approval/signature became obsolete because the group's quorum
  // was already reached by other members.
  if (isSignatureObsolete.value) {
    return {
      text: i18n.t('document_status.quorum_reached_no_action'),
      class: 'text-grey'
    };
  }

  if (isSignaturePending.value || isSignatureOnHold.value) {
    if (isDocumentRejected.value && isSigneeTypeSign.value) {
      return {
        text: i18n.t('titles.signee_rejected_signature'),
        class: 'text-red'
      };
    }
    if (isDocumentRejected.value && isSigneeTypeApprove.value) {
      return {
        text: i18n.t('titles.approver_rejected'),
        class: 'text-red'
      };
    }
    if (
      isSigneeTypeApprove.value &&
      isDocumentInProgress &&
      isSignaturePending.value
    ) {
      return {
        text: i18n.t('titles.waiting_for_approval'),
        class: 'text-grey'
      };
    }
    if (
      isSigneeTypeSign.value &&
      isDocumentInProgress &&
      isSignaturePending.value
    ) {
      return {
        text: i18n.t('titles.waiting_for_signature'),
        class: 'text-grey'
      };
    }
  }

  if (isSignatureRejected.value && isSigneeTypeSign.value) {
    return {
      text: `${i18n.t('titles.signature_rejected')}:`,
      class: 'text-red',
      time: `${formatDateToLocale(
        props.signature.completionTime,
        i18n.locale.value,
        true
      )}`
    };
  }
  if (isSignatureRejected.value && isSigneeTypeApprove.value) {
    return {
      text: `${i18n.t('titles.approval_rejected')}:`,
      class: 'text-red',
      time: `${formatDateToLocale(
        props.signature.completionTime,
        i18n.locale.value,
        true
      )}`
    };
  }

  if (isSignatureApproved.value) {
    return {
      text: `${i18n.t('document_status.approved')}:`,
      class: 'text-green-darken-1',
      time: `${formatDateToLocale(
        props.signature.completionTime,
        i18n.locale.value,
        true
      )}`
    };
  }

  if (isSignatureSigned.value) {
    return {
      text: `${i18n.t('document_status.signed')}:`,
      class: 'text-green-darken-1',
      time: `${formatDateToLocale(
        props.signature.completionTime,
        i18n.locale.value,
        true
      )}`
    };
  }

  if (isSignatureOnHold.value && isSigneeTypeSign.value) {
    return {
      text: `${i18n.t('document_status.on_hold')}`,
      class: 'text-grey'
    };
  }

  if (isSignatureOnHold.value && isSigneeTypeApprove.value) {
    return {
      text: `${i18n.t('document_status.approval_on_hold')}`,
      class: 'text-grey'
    };
  }

  return {
    text: '',
    class: ''
  };
});

const signeeComment = computed(() => {
  if (isMe.value) {
    return (
      signStore.document?.signContext.signeeComment ||
      props.signature?.signeeComment ||
      ''
    );
  }
  return props.signature?.signeeComment || '';
});

const isEllipsisActive = computed(() => {
  if (!emailElement.value) return false;
  return emailElement.value?.scrollWidth > emailElement.value?.offsetWidth;
});

const showResendInvitationLink = computed(
  () =>
    props.isDashBoard &&
    Boolean(props.signature.policy?.canResendInvitation) &&
    Boolean(props.signature.email)
);

function onResendInvitation() {
  if (isPending.value) return;
  const documentId = props.documentId || props.signature.documentId;
  if (!documentId || !props.signature.signeeId || !props.signature.email) {
    return;
  }
  requestResendForSignee({
    documentId,
    signeeId: props.signature.signeeId,
    email: props.signature.email
  });
}

function handleShowDialog() {
  showCommentDialog.value = true;
}

function scrollToSignature() {
  if (
    isMobileOrTabletPortrait.value ||
    isDocumentWithdrawn.value ||
    isDocumentRejected.value ||
    !props.signature.autographPosition
  ) {
    return null;
  }
  SignatureService.scrollToSignaturePosition(props.signature.autographPosition);
}
</script>

<style lang="scss" scoped>
.clickable {
  &:hover {
    cursor: pointer;
  }
}

.signer-card {
  padding-top: 8px;
  min-height: 60px;
}

.signee-email {
  word-break: break-all;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 23px;
}

.invited-signee-email {
  font-size: 14.4px;
  font-weight: 400;
  font-style: normal;
}

.show-comment {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  color: rgb(var(--v-theme-grey-darken-1));

  &:hover {
    color: rgb(var(--v-theme-primary));

    i {
      color: rgb(var(--v-theme-primary));
    }
  }
}

:deep(.v-expansion-panel-text__wrapper) {
  padding-top: 0 !important;
}
</style>
